import debounce from 'debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ConsentPreferredTime } from '../../../../@types/ConsentPreferredTime';
import type { Owner } from '../../../../@types/Owner';
import type { User } from '../../../../@types/User';
import { fetchUsers } from '../../../../store/CoproReducer';
import { useAppDispatch } from '../../../../store/hook';
import type { RootState } from '../../../../store/store';
import { getCompanyTeamRentals, getCompanyTeamTransactions, getCopro } from '../../../../utils/api';
import { getValueByLabel as getFonctionValueByLabel } from '../../../../utils/fonctions';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { isValidEmail, isValidFrenchPhone } from '../../../../utils/validation';
import { showToast } from '../../../common/Notification';
import type { GeocodingFeature, RdvItem, TeamMember } from '../types';
import AgendaPopup from './AgendaPopup';
import ContactInfoSection from './ContactInfoSection';
import ModeSection from './ModeSection';
import NotesActionsSection from './NotesActionsSection';
import PreviewPopup from './PreviewPopup';
import PropertyInfoSection from './PropertyInfoSection';
import TeamAssignmentSection from './TeamAssignmentSection';

interface NewAppointmentFormProps {
  transactionTeam: TeamMember[];
  rentalTeam: TeamMember[];
  onCreateRdv?: (rdv: RdvItem) => void;
  onUpdateRdv?: (id: string, patch: Partial<RdvItem>) => void;
  mode?: 'create' | 'edit';
  initialRdv?: RdvItem;
  coproId?: number; // Needed to create the owner + appointment (create mode)
}

type RequiredFields = {
  civilite: string;
  nomContact: string; // Nom de famille
  prenomContact: string; // Prénom
  telContact: string;
  emailContact: string;
  typeDemande: '' | 'vente' | 'location';
  typeBien: string;
  adresseBien: string;
  dateRdv: string;
  heureRdv: string;
};

interface TeamApiRow {
  id: number;
  firstname?: string | null;
  lastname?: string | null;
  email?: string;
  user?: { email?: string };
  rdvThisWeek?: number;
  rdvNextWeek?: number;
  conversionRate?: number;
}

interface CoproApiItem {
  id: number;
  name?: string;
  addresses?: Array<{ address?: string }>;
}

const initialFields: RequiredFields & { nomImmeuble?: string; notesReferent?: string; recallAt?: string; preferredTime?: ConsentPreferredTime; ownerAddress?: string; ownerPostalCode?: string; ownerCity?: string } = {
  civilite: '',
  nomContact: '',
  prenomContact: '',
  telContact: '',
  emailContact: '',
  typeDemande: '',
  typeBien: '',
  adresseBien: '',
  dateRdv: '',
  heureRdv: '',
  nomImmeuble: '',
  notesReferent: '',
  recallAt: '',
  preferredTime: ConsentPreferredTime.NO_PREFERENCE,
  ownerAddress: '',
  ownerPostalCode: '',
  ownerCity: '',
};

function NewAppointmentForm({ transactionTeam, rentalTeam, onCreateRdv, onUpdateRdv, mode = 'create', initialRdv, coproId }: NewAppointmentFormProps) {
  const jwt = useSelector((s: RootState) => s.auth.token);
  const [fields, setFields] = useState(initialFields);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scheduleNow, setScheduleNow] = useState<boolean>(true);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [collabLoading, setCollabLoading] = useState(false);
  const [collabError, setCollabError] = useState<string | null>(null);
  const [collabList, setCollabList] = useState<TeamMember[]>([]);
  const [collabSearch, setCollabSearch] = useState('');
  const isEdit = mode === 'edit';
  const [isAgendaOpen, setIsAgendaOpen] = useState(false);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [agendaData, setAgendaData] = useState<{ waitingCurrent: Owner[]; waitingNext: Owner[]; toContact: Owner[] } | null>(null);
  const [agendaMember, setAgendaMember] = useState<TeamMember | null>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<Array<{ label: string; value: GeocodingFeature }>>([]);
  const addrAbortControllerRef = useRef<AbortController | null>(null);

  const company = useSelector((s: RootState) => s.auth.company);
  const dispatch = useAppDispatch();
  const users = useSelector((s: RootState) => s.copro.users);

  const [currentCoproId, setCurrentCoproId] = useState<number | null>(coproId ?? null);
  const [coproOptions, setCoproOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [loadingCopros, setLoadingCopros] = useState(false);
  const [errorCopros, setErrorCopros] = useState<string | null>(null);

  const normalizeCivilite = (value: unknown): string => {
    const raw = String(value ?? '').trim();
    if (!raw) { return ''; }
    const s = raw.toLowerCase().replaceAll('.', '').trim();
    if (s === 'm' || s === 'mr' || s === 'monsieur' || raw === '1') { return 'M'; }
    if (s === 'mme' || s === 'madame' || raw === '2') { return 'Mme'; }
    if (s === 'mlle' || s === 'mademoiselle' || raw === '3') { return 'Mlle'; }
    if (raw === 'M' || raw === 'Mme' || raw === 'Mlle') { return raw; }
    return '';
  };

  const normalizeTypeBien = (value: unknown): string => {
    const raw = String(value ?? '').trim();
    const s = raw.toLowerCase();
    if (!s) { return ''; }
    if (s.includes('appart') || /^t\d/.test(s) || s === 'flat' || s.includes('apartment')) { return 'appartement'; }
    if (s.includes('maison') || s.includes('pavillon') || s.includes('villa')) { return 'maison'; }
    if (s.includes('studio')) { return 'studio'; }
    if (s.includes('duplex')) { return 'duplex'; }
    if (s.includes('loft')) { return 'loft'; }
    if (s.includes('local') || s.includes('commerce') || s.includes('commercial') || s.includes('bureau')) { return 'local'; }
    if (s.includes('terrain') || s.includes('parcelle')) { return 'terrain'; }
    if (s.includes('garage') || s.includes('parking') || s.includes('box')) { return 'garage'; }
    return '';
  };

  // Prefill when in edit mode
  useEffect(() => {
    if (!isEdit || !initialRdv) { return; }
    try {
      const fullNameRaw = String(initialRdv.contact?.nom ?? '');
      const parts = fullNameRaw.trim().split(/\s+/).filter(Boolean);
      const prenom = parts.length > 1 ? parts[parts.length - 1] : '';
      const nom = parts.length > 1 ? parts.slice(0, parts.length - 1).join(' ') : (parts[0] ?? '');
      setFields({
        civilite: normalizeCivilite(initialRdv.contact?.civilite),
        nomContact: nom,
        prenomContact: prenom,
        telContact: initialRdv.contact?.tel ?? '',
        emailContact: initialRdv.contact?.email ?? '',
        typeDemande: initialRdv.demande ?? '',
        typeBien: normalizeTypeBien(initialRdv.bien?.type),
        adresseBien: initialRdv.bien?.adresse ?? '',
        dateRdv: initialRdv.date ?? '',
        heureRdv: initialRdv.heure ?? '',
        nomImmeuble: initialRdv.bien?.immeuble ?? '',
        notesReferent: initialRdv.notes ?? '',
      });
      setSelectedMember(initialRdv.assigneA ?? null);
    } catch {
      // noop
    }
  }, [isEdit, initialRdv]);

  // Load company users once to build teams at company scope (not filtered by copro)
  useEffect(() => {
    if (!users || users.length === 0) {
      try { dispatch(fetchUsers(null)); } catch {}
    }
  }, [users?.length, dispatch]);

  const mapUserToTeamMember = (u: User & { name?: string; user?: { email?: string } }, type: 'vente' | 'location'): TeamMember => {
    const first = (u.firstname ?? '').toString();
    const last = (u.lastname ?? '').toString();
    const full = `${first} ${last}`.trim() || (u.name ?? '').toString();
    const initials = `${(first?.[0] ?? (u.name?.[0] ?? ''))?.toUpperCase?.() ?? ''}${(last?.[0] ?? '')?.toUpperCase?.() ?? ''}`.trim() || (full?.[0] ?? '').toUpperCase();
    return {
      id: String(u.id),
      nom: full,
      role: type === 'vente' ? 'Transactionnaire' : 'Gestionnaire Locatif',
      initiales: initials,
      email: (u.email ?? u.user?.email ?? '').toString() || undefined,
      rdvSemaineEnCours: 0,
      rdvSemaineAVenir: 0,
      tauxReussite: 0,
      disponible: true,
      status: 'available',
      type,
    } as TeamMember;
  };

  const mapApiRowToTeamMember = (u: TeamApiRow, type: 'vente' | 'location'): TeamMember => ({
    id: String(u.id),
    nom: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim(),
    role: type === 'vente' ? 'Transactionnaire' : 'Gestionnaire Locatif',
    initiales: `${(u.firstname?.[0] ?? '').toUpperCase()}${(u.lastname?.[0] ?? '').toUpperCase()}`,
    email: (u.email ?? u.user?.email ?? '').toString() || undefined,
    rdvSemaineEnCours: Number(u.rdvThisWeek ?? 0),
    rdvSemaineAVenir: Number(u.rdvNextWeek ?? 0),
    tauxReussite: Math.round(Number(u.conversionRate ?? 0)),
    disponible: true,
    status: 'available',
    type,
  });

  const transactionCompanyTeam = useMemo(() => {
    const role = getFonctionValueByLabel('Transactionnaire'); // numeric code 3
    return (users ?? [])
      .filter((u) => Array.isArray(u.fonctions) && u.fonctions.some((f) => f.type === role))
      .map((u) => mapUserToTeamMember(u, 'vente'));
  }, [users]);

  const rentalCompanyTeam = useMemo(() => {
    const role = getFonctionValueByLabel('Gestionnaire'); // numeric code 7
    return (users ?? [])
      .filter((u) => Array.isArray(u.fonctions) && u.fonctions.some((f) => f.type === role))
      .map((u) => mapUserToTeamMember(u, 'location'));
  }, [users]);

  const team = useMemo(() => (fields.typeDemande === 'vente'
    ? (transactionCompanyTeam.length > 0 ? transactionCompanyTeam : transactionTeam)
    : fields.typeDemande === 'location'
      ? (rentalCompanyTeam.length > 0 ? rentalCompanyTeam : rentalTeam)
      : []), [fields.typeDemande, transactionTeam, rentalTeam, transactionCompanyTeam, rentalCompanyTeam]);

  const displayTeam: TeamMember[] = useMemo(() => {
    if (collabList.length > 0) { return collabList; }
    return team;
  }, [collabList, team]);

  const filteredDisplayTeam: TeamMember[] = useMemo(() => {
    if (!collabSearch.trim()) { return displayTeam; }
    const q = collabSearch.toLowerCase();
    return displayTeam.filter(m => m.nom.toLowerCase().includes(q) || m.role.toLowerCase().includes(q));
  }, [displayTeam, collabSearch]);

  const setField = (k: keyof typeof initialFields, v: string) => setFields(prev => ({ ...prev, [k]: v }));

  const autoCompleteAdresseBien = useCallback((query: string) => {
    if (addrAbortControllerRef.current) {
      try { addrAbortControllerRef.current.abort(); } catch {}
    }
    addrAbortControllerRef.current = new AbortController();
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5&type=housenumber`, {
      signal: addrAbortControllerRef.current.signal,
    })
      .then((response) => response.json())
      .then((result) => {
        const suggestions = Array.isArray(result?.features)
          ? result.features.map((feature: GeocodingFeature) => ({
              label: `${feature?.properties?.name ?? ''} ${feature?.properties?.postcode ?? ''} ${feature?.properties?.city ?? ''}`.trim(),
              value: feature,
            }))
          : [];
        setAddressSuggestions(suggestions);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError' && error?.name !== 'TypeError') {
          showToast('error', "Une erreur est survenue lors de l'autocomplétion d'adresse.");
        }
      });
  }, []);

  const debouncedAutoCompleteAdresseBien = useMemo(() =>
    debounce(autoCompleteAdresseBien, 300),
    [autoCompleteAdresseBien],
  );

  const handleAddressSuggestionClick = (feature: GeocodingFeature) => {
    const street = feature?.properties?.name ?? '';
    const postalCode = feature?.properties?.postcode ?? '';
    const city = feature?.properties?.city ?? '';
    const full = [street, postalCode, city].filter(Boolean).join(' ');
    setFields(prev => ({
      ...prev,
      adresseBien: full,
      ownerAddress: street,
      ownerPostalCode: postalCode,
      ownerCity: city,
    }));
    setAddressSuggestions([]);
  };

  // Fetch collaborators from company endpoints when typeDemande changes
  useEffect(() => {
    const load = async() => {
      if (!isEdit) { setSelectedMember(null); }
      setCollabError(null);
      setCollabList([]);
      if (!company?.id || !jwt) { return; }
      if (fields.typeDemande !== 'vente' && fields.typeDemande !== 'location') { return; }
      setCollabLoading(true);
      const headers = JWTHeader(jwt);
      try {
        const url = fields.typeDemande === 'vente'
          ? getCompanyTeamTransactions(company.id)
          : getCompanyTeamRentals(company.id);
        const res = await fetch(url, headers);
        if (!res.ok) { throw new Error(''); }
        const arr = await res.json();
        const mapped = (arr as TeamApiRow[]).map((u) => mapApiRowToTeamMember(u, fields.typeDemande as 'vente' | 'location'));
        setCollabList(mapped);
      } catch {
        setCollabError('Impossible de charger les collaborateurs.');
        setCollabList([]);
      } finally {
        setCollabLoading(false);
      }
    };
    load();
  }, [fields.typeDemande, company?.id, jwt, isEdit]);

  // Load copros for the select
  useEffect(() => {
    const load = async() => {
      if (!company?.id || !jwt) { return; }
      setLoadingCopros(true); setErrorCopros(null);
      try {
        const res = await fetch(getCopro(company.id, true), JWTHeader(jwt));
        if (!res.ok) { throw new Error(''); }
        const data = await res.json();
        const options = (data.results ?? data ?? []).map((c: CoproApiItem) => ({ value: Number(c.id), label: c.name ?? (c.addresses?.[0]?.address ?? `Copro #${c.id}`) }));
        setCoproOptions(options);
        if (!currentCoproId && options.length > 0) { setCurrentCoproId(options[0].value); }
      } catch {
        setErrorCopros('Impossible de charger les copropriétés.');
      } finally {
        setLoadingCopros(false);
      }
    };
    load();
  }, [company?.id, jwt]);

  // No copro filtering for teams; teams come from company-wide users filtered by role

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    const req: (keyof RequiredFields)[] = ['civilite', 'nomContact', 'prenomContact', 'telContact', 'emailContact', 'typeDemande', 'typeBien', 'adresseBien'];
    if (scheduleNow) {
      req.push('dateRdv', 'heureRdv');
    }
    if (!scheduleNow && !String(fields.recallAt || '').trim()) { next.recallAt = 'Champ obligatoire'; }
    req.forEach(k => { if (!String(fields[k]).trim()) { next[k] = 'Champ obligatoire'; } });
    if (fields.emailContact && !isValidEmail(fields.emailContact)) { next.emailContact = 'Email invalide'; }
    if (fields.telContact && !isValidFrenchPhone(fields.telContact)) { next.telContact = 'Téléphone invalide'; }
    if (!selectedMember) { next.assigneA = 'Sélectionnez un collaborateur'; }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async() => {
    if (!validate() || !selectedMember) { return; }

    // If edit mode, update existing RDV and return
    if (isEdit && initialRdv) {
      const updated: RdvItem = {
        ...initialRdv,
        date: fields.dateRdv,
        heure: fields.heureRdv,
        contact: { civilite: fields.civilite, nom: [fields.nomContact, fields.prenomContact].filter(Boolean).join(' '), tel: fields.telContact, email: fields.emailContact },
        bien: { type: fields.typeBien, adresse: fields.adresseBien, immeuble: fields.nomImmeuble || undefined },
        demande: fields.typeDemande as 'vente' | 'location',
        assigneA: selectedMember,
        notes: fields.notesReferent || undefined,
      };
      try { onUpdateRdv?.(initialRdv.id, updated); } catch {}
      return;
    }

    // Build API payload for incoming-call appointment (create mode)
    const payload = {
      civilite: fields.civilite,
      nomContact: [fields.nomContact, fields.prenomContact].filter(Boolean).join(' '),
      telContact: fields.telContact,
      emailContact: fields.emailContact,
      typeDemande: fields.typeDemande, // 'vente' | 'location'
      typeBien: fields.typeBien,
      adresseBien: fields.adresseBien,
      ownerAddress: fields.ownerAddress || undefined,
      ownerPostalCode: fields.ownerPostalCode || undefined,
      ownerCity: fields.ownerCity || undefined,
      // date/heure only when scheduling now
      dateRdv: scheduleNow ? fields.dateRdv : undefined,
      heureRdv: scheduleNow ? fields.heureRdv : undefined,
      assignedUserId: parseInt(String(selectedMember.id), 10),
      notesReferent: fields.notesReferent || '',
      scheduleNow,
      // recall info only when not scheduling now
      recallAt: !scheduleNow ? (fields.recallAt || undefined) : undefined,
      preferredTime: !scheduleNow ? (fields.preferredTime || undefined) : undefined,
    };

    try {
      if (!currentCoproId || !jwt) {
        showToast('error', 'Contexte manquant: copropriété ou session.');
        return;
      }

      const res = await fetch(`/api/copros/${currentCoproId}/incoming-call-appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // Best-effort message, prefer toast over alert
        showToast('error', 'Echec de création du rendez-vous.');
        return;
      }

      if (!scheduleNow) {
        // Success toast only for lead "à contacter" creation
        showToast('success', 'Le lead à contacter a bien été créé.');
      } else {
        // Only create local RDV and trigger success flow after successful request
        const rdv: RdvItem = {
          id: `rdv-${Date.now()}`,
          date: fields.dateRdv,
          heure: fields.heureRdv,
          contact: { civilite: fields.civilite, nom: [fields.nomContact, fields.prenomContact].filter(Boolean).join(' '), tel: fields.telContact, email: fields.emailContact },
          bien: { type: fields.typeBien, adresse: fields.adresseBien, immeuble: fields.nomImmeuble || undefined },
          demande: fields.typeDemande as 'vente' | 'location',
          assigneA: selectedMember,
          notes: fields.notesReferent || undefined,
          statut: 'programme',
          emailSent: false,
          emailPending: false,
          emailSentDate: null,
        };
        try { onCreateRdv?.(rdv); } catch {}
      }

      // Reset form only on success
      setFields(prev => ({ ...initialFields, dateRdv: prev.dateRdv, heureRdv: prev.heureRdv }));
      setSelectedMember(null);
      setErrors({});
    } catch {
      showToast('error', 'Erreur réseau lors de la création du rendez-vous.');
    }
  };

  const handlePreview = () => {
    if (!validate() || !selectedMember) { return; }
    setIsPreviewOpen(true);
  };

  const formatDateFR = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  // default date = tomorrow
  React.useEffect(() => {
    if (!fields.dateRdv) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setField('dateRdv', tomorrow.toISOString().split('T')[0]);
    }
   
  }, []);

  const openAgendaFor = async(member: TeamMember) => {
    try {
      setAgendaLoading(true);
      setIsAgendaOpen(true);
      setAgendaMember(member);
      const kind = member.type === 'vente' ? 'vente' : 'location';
      const res = await fetch(`/api/users/${member.id}/agenda?kind=${encodeURIComponent(kind)}`, JWTHeader(jwt));
      if (!res.ok) {
        setAgendaData(null);
        showToast('error', "Impossible de charger l'agenda du collaborateur.");
        return;
      }
      const data = await res.json();
      setAgendaData({
        waitingCurrent: data.waitingCurrent ?? [],
        waitingNext: data.waitingNext ?? [],
        toContact: data.toContact ?? [],
      });
    } catch {
      setAgendaData(null);
      showToast('error', "Erreur lors du chargement de l'agenda.");
    } finally {
      setAgendaLoading(false);
    }
  };

  const formatDateTimeFR = (dateString?: string | null): string => {
    if (!dateString) { return ''; }
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) { return ''; }
      return d.toLocaleString('fr-FR', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getOwnerFullName = (o: Owner): string => {
    const first = (o?.firstname ?? '').toString().trim();
    const last = (o?.lastname ?? '').toString().trim();
    return [last, first].filter(Boolean).join(' ');
  };

  const getOwnerPhone = (o: Owner): string => (o?.phone ?? '').toString().trim();

  const getOwnerMeetingAt = (o: Owner): string => {
    const list = Array.isArray(o?.qualityReferenceInteractions) ? o.qualityReferenceInteractions : [];
    const withDates = list.map((i) => (i as { meetingAt?: string | null })?.meetingAt).filter(Boolean) as string[];
    if (withDates.length === 0) { return ''; }
    const sorted = withDates.map((s) => new Date(s)).filter((d) => !Number.isNaN(d.getTime())).sort((a, b) => a.getTime() - b.getTime());
    if (sorted.length === 0) { return ''; }
    return formatDateTimeFR(sorted[0].toISOString());
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedAutoCompleteAdresseBien.clear?.();
    };
  }, [debouncedAutoCompleteAdresseBien]);

  return (
    <section className="rounded-xl bg-white p-2 shadow-md">
      <h2 className="mb-4 text-lg font-bold text-rgpd-blue">
        {isEdit ? (
          <span><span role="img" aria-label="pencil">✏️</span> Modifier le Rendez-vous</span>
        ) : (
          <span><span role="img" aria-label="telephone">📞</span> Nouveau Rendez-vous</span>
        )}
      </h2>
      
      <ContactInfoSection
        fields={{ civilite: fields.civilite, prenomContact: fields.prenomContact, nomContact: fields.nomContact, telContact: fields.telContact, emailContact: fields.emailContact }}
        setField={(k, v) => setField(k as keyof typeof initialFields, v)}
        errors={errors}
      />

      <PropertyInfoSection
        isEdit={isEdit}
        fields={{ typeDemande: fields.typeDemande, typeBien: fields.typeBien, adresseBien: fields.adresseBien }}
        setField={(k, v) => {
          if (k === 'typeDemande') { setSelectedMember(null); }
          setField(k as keyof typeof initialFields, v);
        }}
        errors={errors}
        loadingCopros={loadingCopros}
        errorCopros={errorCopros}
        coproOptions={coproOptions}
        currentCoproId={currentCoproId}
        setCurrentCoproId={(id) => setCurrentCoproId(id)}
        addressSuggestions={addressSuggestions}
        onAdresseChange={(value) => { setField('adresseBien', value); debouncedAutoCompleteAdresseBien(value); }}
        onAddressSuggestionClick={handleAddressSuggestionClick}
      />

      <ModeSection
        scheduleNow={scheduleNow}
        setScheduleNow={setScheduleNow}
        fields={{ recallAt: fields.recallAt || '', preferredTime: fields.preferredTime || ConsentPreferredTime.NO_PREFERENCE, dateRdv: fields.dateRdv, heureRdv: fields.heureRdv }}
        setField={(k, v) => setField(k as keyof typeof initialFields, v)}
        errors={errors}
      />

      <TeamAssignmentSection
        teamType={fields.typeDemande}
        collabLoading={collabLoading}
        collabError={collabError}
        displayTeam={displayTeam}
        filteredDisplayTeam={filteredDisplayTeam}
        collabSearch={collabSearch}
        setCollabSearch={setCollabSearch}
        selectedMember={selectedMember}
        setSelectedMember={(m) => setSelectedMember(m)}
        errorAssign={errors.assigneA}
        onOpenAgenda={openAgendaFor}
      />

      <NotesActionsSection
        notes={fields.notesReferent || ''}
        setNotes={(v) => setField('notesReferent', v)}
        isEdit={isEdit}
        scheduleNow={scheduleNow}
        onPreview={handlePreview}
        onSubmit={handleSubmit}
        onReset={() => { setFields(initialFields); setSelectedMember(null); setErrors({}); }}
      />

      <AgendaPopup
        isOpen={isAgendaOpen}
        onClose={() => setIsAgendaOpen(false)}
        titleSuffix={agendaMember ? ` — ${agendaMember.nom}` : ''}
        agendaLoading={agendaLoading}
        agendaData={agendaData}
        getOwnerFullName={getOwnerFullName}
        getOwnerPhone={getOwnerPhone}
        getOwnerMeetingAt={getOwnerMeetingAt}
      />

      {scheduleNow && (
        <PreviewPopup
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
          fields={{
            typeDemande: fields.typeDemande,
            typeBien: fields.typeBien,
            dateRdv: fields.dateRdv,
            heureRdv: fields.heureRdv,
            civilite: fields.civilite,
            nomContact: fields.nomContact,
            telContact: fields.telContact,
            emailContact: fields.emailContact,
            adresseBien: fields.adresseBien,
            notesReferent: fields.notesReferent,
          }}
          selectedMemberName={selectedMember?.nom}
          selectedMemberRole={selectedMember?.role}
          formatDateFR={formatDateFR}
          onConfirm={() => { setIsPreviewOpen(false); handleSubmit(); }}
        />
      )}
    </section>
  );
}

export default NewAppointmentForm;


